const { Analytics } = require('@segment/analytics-node');
const sanitizeHtml = require('sanitize-html');
const _ = require('lodash');
const { BASEURL } = require('./config');
const { composeUrlWithQuery } = require('./utils');

const analytics = new Analytics({ writeKey: 'S6OQ0bW0mo6zHhL4A4p6zvKJLXTgmBHx' });

function applicantClickedSMSLink(applicant, requestUrl) {
  const {
    creator_id: userId,
    id: positionApplicationId,
    company_id: companyId,
    sms_phone_number: fromPhone,
    global_phone_number_for_sms: toPhone,
  } = applicant;
  analytics.track({
    userId: String(userId),
    event: 'Applicant Clicked SMS Link',
    properties: {
      position_application_id: positionApplicationId,
      company_id: companyId,
      from_phone: fromPhone,
      to_phone: toPhone,
      url: requestUrl,
    },
  });
}

function constructDashString(company, location, position) {
  // this function will return string like this: "company.name - location.name - position.name"
  // from company/location/position object;
  const s = _.compact([(company || {}).name, (location || {}).name, (position || {}).title])
    .join(' - ');
  return s;
}

function isMobile(req) {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(req.headers['user-agent']);
}

function sanitizeIframe(content) {
  return sanitizeHtml(
    content,
    {
      allowedTags: ['iframe'],
      allowedAttributes: { iframe: ['src', 'href', 'width', 'height', 'scrolling', 'frameborder', 'allowfullscreen', 'style'] },
    },
  );
}

function generateOgData(organization, request) {
  if (!organization) return {};

  const {
    slug = '',
    name = '',
    introduction = '',
    logo_width: logoWidth = '',
    logo_height: logoHeight = '',
    logo = {},
  } = organization;

  const { url: logoUrl = '' } = logo || {};
  const { url: requestUrl = '' } = request || {};
  const url = requestUrl ? `${BASEURL}/j${requestUrl}` : `${BASEURL}/j/${slug}`;

  return {
    url,
    imageWidth: logoWidth || '',
    imageHeight: logoHeight || '',
    image: logoUrl.replace('https://', 'http://'),
    imageSecureUrl: logoUrl,
    title: name,
    description: (introduction || '').replace(/^(.{160}[^\s]*).*/, '$1'),
  };
}

function replaceWOTCQuestion(questions, formKey, t) {
  const wotcIndex = questions.findIndex((question) => question.question_type === 'wotc_questionnaire');

  if (wotcIndex === -1) return questions;

  const yesOrNoOptions = [
    {
      label: t('Yes'),
      value: 'Yes',
    },
    {
      label: t('No'),
      value: 'No',
    },
  ];

  const wotcQuestion = questions[wotcIndex];
  const wotcQuestions = [
    {
      id: `${wotcQuestion.id}_is_under_40`,
      question_type: 'wotc_questionnaire',
      title: t('Are you under age 40?'),
      is_required: true,
      options: yesOrNoOptions,
      name: `${formKey}[${wotcQuestion.id}][answer][is_under_40]`,
      sequence: wotcQuestion.sequence,
    },
    {
      id: `${wotcQuestion.id}_has_snap`,
      question_type: 'wotc_questionnaire',
      title: t('Do you, or your family, receive SNAP Benefits? (Food Stamps or Cash Assistance)'),
      is_required: true,
      options: yesOrNoOptions,
      name: `${formKey}[${wotcQuestion.id}][answer][has_snap]`,
      sequence: wotcQuestion.sequence,
    },
    {
      id: `${wotcQuestion.id}_has_ssi`,
      question_type: 'wotc_questionnaire',
      title: t('Do you receive SSI Benefits?'),
      is_required: true,
      options: yesOrNoOptions,
      name: `${formKey}[${wotcQuestion.id}][answer][has_ssi]`,
      sequence: wotcQuestion.sequence,
    },
    {
      id: `${wotcQuestion.id}_is_veteran`,
      question_type: 'wotc_questionnaire',
      title: t('Are you a US Veteran?'),
      is_required: true,
      options: yesOrNoOptions,
      name: `${formKey}[${wotcQuestion.id}][answer][is_veteran]`,
      sequence: wotcQuestion.sequence,
    },
    {
      id: `${wotcQuestion.id}_is_felon`,
      question_type: 'wotc_questionnaire',
      title: t('Are you an ex-felon?'),
      is_required: true,
      options: yesOrNoOptions,
      name: `${formKey}[${wotcQuestion.id}][answer][is_felon]`,
      sequence: wotcQuestion.sequence,
    },
    {
      id: `${wotcQuestion.id}_is_unemployed`,
      question_type: 'wotc_questionnaire',
      title: t('Have you been unemployed for 6 months?'),
      is_required: true,
      options: yesOrNoOptions,
      name: `${formKey}[${wotcQuestion.id}][answer][is_unemployed]`,
      sequence: wotcQuestion.sequence,
    },
  ];

  questions.splice(wotcIndex, 1, ...wotcQuestions);
  return questions;
}

function slugify(string) {
  return string
    .toLowerCase()
    .normalize('NFD')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');
}

function replaceAll(str, mapObj) {
  const re = new RegExp(Object.keys(mapObj).join('|'), 'gi');

  return str.replace(re, (matched) => mapObj[matched.toLowerCase()]);
}

function locationPageUrl(slug, location, digest, locale = 'en') {
  const template = `${BASEURL}/j/slug-careers-location/l-digest?locale=${locale}`;
  return replaceAll(template, { slug, location: slugify(location), digest });
}

function positionDetailUrl(slug, location, position, digest, locale = 'en') {
  const template = `${BASEURL}/j/slug-careers-location-position/p-digest?locale=${locale}`;
  return replaceAll(template, {
    slug,
    location: slugify(location),
    position: slugify(position),
    digest,
  });
}

const CAREER_PAGE_TRACKING_EVENTS = {
  view_corporation_level_career_page:
    'hiring - view corporation level career page',
  view_company_level_career_page:
    'hiring - view company level career page',
  view_brand_level_career_page:
    'hiring - view brand level career page',
  view_location_level_career_page:
    'hiring - view location level career page',
  view_position_detail_page: 'hiring - view position detail page',
  start_application_form: 'hiring - start application form',
  complete_application_form: 'hiring - complete application form',
};

const chatBotTrackingPages = {
  corporation_career_page: 'corporation-page',
  company_career_page: 'company-page',
  brand_career_page: 'brand-page',
  search_result_career_page: 'search-result-page',
  position_list_career_page: 'position-list-page',
  location_list_career_page: 'location-list-page',
  position_detail_career_page: 'position-detail-page',
  location_detail_career_page: 'location-detail-page',
  application_form_career_page: 'application-form-page',
  application_form_career_apply_success_page: 'application-form-apply-success-page',
};

const createDatePickerI18n = (t) => ({
  previousMonth: t('enums:datepicker.previousMonth', 'Previous Month'),
  nextMonth: t('enums:datepicker.nextMonth', 'Next Month'),
  months: [
    t('enums:datepicker.January', 'January'),
    t('enums:datepicker.February', 'February'),
    t('enums:datepicker.March', 'March'),
    t('enums:datepicker.April', 'April'),
    t('enums:datepicker.May', 'May'),
    t('enums:datepicker.June', 'June'),
    t('enums:datepicker.July', 'July'),
    t('enums:datepicker.August', 'August'),
    t('enums:datepicker.September', 'September'),
    t('enums:datepicker.October', 'October'),
    t('enums:datepicker.November', 'November'),
    t('enums:datepicker.December', 'December'),
  ],
  weekdays: [
    t('enums:datepicker.Sunday', 'Sunday'),
    t('enums:datepicker.Monday', 'Monday'),
    t('enums:datepicker.Tuesday', 'Tuesday'),
    t('enums:datepicker.Wednesday', 'Wednesday'),
    t('enums:datepicker.Thursday', 'Thursday'),
    t('enums:datepicker.Friday', 'Friday'),
    t('enums:datepicker.Saturday', 'Saturday'),
  ],
  weekdaysShort: [
    t('enums:datepicker.Sun', 'Sun'),
    t('enums:datepicker.Mon', 'Mon'),
    t('enums:datepicker.Tue', 'Tue'),
    t('enums:datepicker.Wed', 'Wed'),
    t('enums:datepicker.Thu', 'Thu'),
    t('enums:datepicker.Fri', 'Fri'),
    t('enums:datepicker.Sat', 'Sat'),
  ],
});

// For SEO
function formatPageInfo({ title, description }) {
  return {
    title: _.truncate(title, {
      length: 58, // include the ellipsis
      separator: ' ', // break on space
    }),
    description: _.truncate(description, {
      length: 158, // include the ellipsis
      separator: /,? +/, // break on comma or space
    }),
  };
}

/**
 * Set company attribute for GrowthBook
 * @param {Object} req - Express request object
 * @param {Object} company - Company object with id property
 */
function setGrowthBookCompanyAttribute(req, company) {
  if (company && req.growthbook) {
    const attributes = {
      // Only update the `company` attribute, keep the rest the same
      ...req.growthbook.getAttributes(),
      ...(company.id ? { company: company.id.toString() } : {}),
      ...(company.status ? { company_status: company.status } : {}),
      ...(company.company_created_at ? { company_created_at: company.company_created_at } : {}),
      ...(company.brands ? { base_brand_ids: _.compact(company.brands.map((brand) => brand.base_brand_id)) } : {}),
    };

    req.growthbook.setAttributes(attributes);
  }
}

function getLanguageRedirectUrl(req, brand, position) {
  const i18next = req.i18n;
  const supportedLanguages = _.intersection(
    _.get(brand, 'languages', []),
    _.get(position, 'languages', []),
  );
  const suggestLanguage = _.find([i18next.resolvedLanguage, req.query.locale || 'en'], (lang) => supportedLanguages.includes(lang)) || 'en';

  // Redirect when
  // 1. locale is provided but not supported
  // 2. locale is not provided and suggested language is not 'en'
  if ((req.query.locale && req.query.locale !== 'en' && !supportedLanguages.includes(req.query.locale)) || (!req.query.locale && suggestLanguage !== 'en')) {
    return `/j${composeUrlWithQuery(req.path, {
      ...req.query,
      locale: suggestLanguage,
    })}`;
  }
  return null;
}

// Returns a JS string literal safe to embed in inline <script>. Use as
// `var x = !{safeQueryValue(value)};` in pug. [SEC-554]
const serialize = require('serialize-javascript');

function safeQueryValue(value) {
  if (value === null || value === undefined) {
    return serialize('', { isJSON: true });
  }
  return serialize(String(value), { isJSON: true });
}

function buildJobPageAppViewData(t, {
  onelink, digestKey, scope = {}, inAppWebview = false,
}) {
  if (!onelink || inAppWebview) {
    return {
      jobPageAppDownloadOnelink: null,
      jobPageAppConfigJson: '',
      jobPageAppI18nJson: '',
    };
  }

  const cleanScope = {};
  if (scope.company_digest_key) cleanScope.company_digest_key = scope.company_digest_key;
  if (scope.brand_friendly_id) cleanScope.brand_friendly_id = scope.brand_friendly_id;
  if (scope.location_id) cleanScope.location_id = scope.location_id;
  if (scope.slug) cleanScope.slug = scope.slug;

  return {
    jobPageAppDownloadOnelink: onelink,
    jobPageAppConfigJson: JSON.stringify({
      onelink,
      digestKey,
      scope: cleanScope,
    }),
    jobPageAppI18nJson: JSON.stringify({
      invalid_phone: t('Enter a valid phone number.'),
      sms_sent: t("If SMS is enabled for this number, you'll receive the link shortly."),
      sms_cooldown: t('Resend in {{seconds}}s'),
      sms_link_unavailable: t('App download link is not available.'),
      sms_suppressed: t('This phone number cannot receive messages.'),
      sms_send_failed: t('Unable to send the link. Please try again.'),
      sms_rate_limited: t('Please wait {{seconds}} seconds before requesting another link.'),
      sms_too_many_requests: t('Too many requests. Please try again later.'),
      sms_error: t('Unable to send SMS. Please try again.'),
    }),
  };
}

module.exports = {
  slugify,
  applicantClickedSMSLink,
  constructDashString,
  isMobile,
  sanitizeIframe,
  generateOgData,
  replaceWOTCQuestion,
  locationPageUrl,
  positionDetailUrl,
  createDatePickerI18n,
  CAREER_PAGE_TRACKING_EVENTS,
  chatBotTrackingPages,
  formatPageInfo,
  setGrowthBookCompanyAttribute,
  getLanguageRedirectUrl,
  safeQueryValue,
  buildJobPageAppViewData,
};
